import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Alert,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { Group, Schedule } from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { allocationsAPI } from '../services/api';
import { batchesAPI } from '../services/batches';
import TimeSelector from './TimeSelector';
import {
  getAvailableTimezones,
  getTimezoneLocation,
  normalizeTimezoneForFormSelect,
  normalizeTimezoneForStorage,
} from '../utils/timezoneUtils';
import { getTimezone } from '../data/timezones';
import { useAuth } from '../contexts/AuthContext';
import { LabelWithInfo } from './InfoTooltip';
import BatchAssignedStudentsPanel from './BatchAssignedStudentsPanel';
import { unwrapBatchDetail, getBatchStudents, resolveBatchStudentCount } from '../utils/batchDetail';
import { parseSubstituteTeachersResponse } from './Teacher/SubstituteTeacherPicker';
import BatchScheduleTeacherResults from './BatchScheduleTeacherResults';
import { getCountries } from '../data/locationData';
import { getAgeGroupSelectOptions } from '../constants/ageGroups';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hh, mm] = String(timeStr).split(':').map(Number);
  return hh * 60 + mm;
}

function isActiveFlag(value) {
  if (value === false || value === 0 || value === 'false' || value === 'f') return false;
  return true;
}

function buildSchedulePayload(schedules) {
  const scheduleData = [];
  Object.entries(schedules || {}).forEach(([day, slots]) => {
    (slots || []).forEach((slot) => {
      if (slot.startTime && slot.endTime) {
        const durationMinutes = toMinutes(slot.endTime) - toMinutes(slot.startTime);
        if (durationMinutes > 0) {
          scheduleData.push({
            day_of_week: parseInt(day, 10),
            start_time: slot.startTime + ':00',
            end_time: slot.endTime + ':00',
            duration_minutes: durationMinutes,
          });
        }
      }
    });
  });
  return scheduleData;
}

const BatchCreationForm = ({ open, onClose, onSuccess, mode = 'create', initialData = null, batchId = null, prefill = null }) => {
  const queryClient = useQueryClient();
  const { canManageBatches } = useAuth();
  const canManageAssignments = canManageBatches();

  const [formData, setFormData] = useState({
    name: '',
    country: '',
    timezone: '',
    batch_type: 'group',
    age_group: '',
    max_capacity: 10,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    teacher_id: '',
    secondary_teacher_id: '',
    is_inclusive: false,
    status: 'active'
  });

  const [selectedTeacherTimezone, setSelectedTeacherTimezone] = useState('');

  const [schedules, setSchedules] = useState({
    0: [], // Sunday
    1: [], // Monday
    2: [], // Tuesday
    3: [], // Wednesday
    4: [], // Thursday
    5: [], // Friday
    6: []  // Saturday
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [partialTeachers, setPartialTeachers] = useState([]);
  const [unavailableTeachers, setUnavailableTeachers] = useState([]);
  const [loadingAvailableTeachers, setLoadingAvailableTeachers] = useState(false);
  const [teacherPicker, setTeacherPicker] = useState(null);
  const [addSlotDay, setAddSlotDay] = useState(1);
  const [addSlotStart, setAddSlotStart] = useState('06:00');
  const [addSlotEnd, setAddSlotEnd] = useState('06:45');
  const [unassigningAssignmentId, setUnassigningAssignmentId] = useState(null);
  const [namePreview, setNamePreview] = useState('');

  const { data: batchLiveResponse, isLoading: loadingAssignedStudents } = useQuery(
    ['batch', batchId],
    () => batchesAPI.getById(batchId),
    {
      enabled: open && mode === 'edit' && !!batchId,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );

  const liveBatch = unwrapBatchDetail(batchLiveResponse);
  const assignedStudents = liveBatch ? getBatchStudents(liveBatch) : getBatchStudents(initialData);
  const currentStudentCount = resolveBatchStudentCount(liveBatch || initialData);

  const pickerLists = useMemo(() => {
    const available = [...availableTeachers];
    const partial = [...partialTeachers];
    const unavailable = [...unavailableTeachers];
    const known = new Set(
      [...available, ...partial, ...unavailable].map((t) => String(t.id))
    );

    const inject = (id, name, email, timezone, reasonLabel = 'Current assignment', isActive = true) => {
      if (!id || known.has(String(id))) return;
      unavailable.push({
        id: String(id),
        name: name || 'Assigned teacher',
        email: email || '',
        timezone: timezone || 'UTC',
        reason: isActive ? 'busy' : 'inactive',
        reasonLabel: isActive ? reasonLabel : 'Inactive',
        isActive,
      });
      known.add(String(id));
    };

    if (mode === 'edit' && initialData) {
      inject(
        initialData.teacher_id,
        initialData.teacher_name,
        initialData.teacher_email,
        initialData.teacher_timezone,
        'Current assignment',
        isActiveFlag(initialData.teacher_is_active)
      );
      inject(
        initialData.secondary_teacher_id,
        initialData.secondary_teacher_name,
        initialData.secondary_teacher_email,
        null,
        'Current assignment',
        isActiveFlag(initialData.secondary_teacher_is_active)
      );
    }
    if (mode === 'create' && prefill?.teacher_id) {
      inject(
        prefill.teacher_id,
        prefill.teacher_name,
        prefill.teacher_email,
        prefill.teacher_timezone,
        'Selected from search'
      );
    }

    return { available, partial, unavailable };
  }, [availableTeachers, partialTeachers, unavailableTeachers, mode, initialData, prefill]);

  const teachersById = useMemo(() => {
    const map = {};
    [...pickerLists.available, ...pickerLists.partial, ...pickerLists.unavailable].forEach((t) => {
      map[String(t.id)] = t;
    });
    return map;
  }, [pickerLists]);

  const hasConfiguredSchedules = Object.values(schedules).some((slots) => slots.length > 0);
  const configuredSlots = useMemo(() => {
    const rows = [];
    for (let day = 0; day <= 6; day += 1) {
      (schedules[day] || []).forEach((slot, index) => {
        rows.push({ day, index, startTime: slot.startTime, endTime: slot.endTime });
      });
    }
    return rows;
  }, [schedules]);

  const assignedTeacher = (role) => {
    const id = role === 'secondary' ? formData.secondary_teacher_id : formData.teacher_id;
    if (!id) return null;
    const fromLists = teachersById[String(id)];
    if (role === 'secondary') {
      return {
        id,
        name: fromLists?.name || initialData?.secondary_teacher_name || 'Assigned teacher',
        email: fromLists?.email || initialData?.secondary_teacher_email || '',
        isActive: fromLists
          ? (fromLists.isActive != null ? isActiveFlag(fromLists.isActive) : true)
          : isActiveFlag(initialData?.secondary_teacher_is_active),
      };
    }
    return {
      id,
      name: fromLists?.name || initialData?.teacher_name || prefill?.teacher_name || 'Assigned teacher',
      email: fromLists?.email || initialData?.teacher_email || prefill?.teacher_email || '',
      isActive: fromLists
        ? (fromLists.isActive != null ? isActiveFlag(fromLists.isActive) : true)
        : isActiveFlag(initialData?.teacher_is_active),
    };
  };

  // Initialize form data when in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialData && open) {
      // Always reset schedules first to clear any previous state
      const convertedSchedules = {
        0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: []
      };

      // Set form data from initial data
      setFormData({
        name: initialData.name || '',
        country: initialData.country || '',
        timezone: initialData.timezone
          ? normalizeTimezoneForFormSelect(initialData.timezone)
          : '',
        batch_type: initialData.batch_type || 'group',
        age_group: initialData.age_group || '',
        max_capacity: initialData.max_capacity || 10,
        start_date: initialData.start_date ? initialData.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
        end_date: initialData.end_date ? initialData.end_date.split('T')[0] : '',
        teacher_id: initialData.teacher_id || '',
        secondary_teacher_id: initialData.secondary_teacher_id || '',
        is_inclusive: initialData.is_inclusive || false,
        status: initialData.status || 'active'
      });

      // Set teacher timezone
      if (initialData.teacher_timezone) {
        setSelectedTeacherTimezone(initialData.teacher_timezone);
      }

      // Convert schedules from backend format to frontend format
      if (initialData.schedules && Array.isArray(initialData.schedules)) {
        initialData.schedules.forEach(schedule => {
          const day = schedule.day_of_week;
          if (day >= 0 && day <= 6) {
            // Convert "09:00:00" to "09:00"
            const startTime = schedule.start_time ? schedule.start_time.substring(0, 5) : '';
            const endTime = schedule.end_time ? schedule.end_time.substring(0, 5) : '';
            
            if (startTime && endTime) {
              convertedSchedules[day].push({
                startTime,
                endTime,
                isActive: true
              });
            }
          }
        });
      }
      
      // Set schedules after conversion
      setSchedules(convertedSchedules);
    } else if (mode === 'create' && open) {
      if (prefill && (prefill.teacher_id || (prefill.schedules && Object.values(prefill.schedules).some(s => s.length > 0)))) {
        const convertedSchedules = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
        if (prefill.schedules && typeof prefill.schedules === 'object') {
          Object.entries(prefill.schedules).forEach(([day, slots]) => {
            const d = parseInt(day, 10);
            if (d >= 0 && d <= 6 && Array.isArray(slots)) {
              convertedSchedules[d] = slots.map(s => ({
                startTime: s.startTime || (s.start_time ? String(s.start_time).substring(0, 5) : ''),
                endTime: s.endTime || (s.end_time ? String(s.end_time).substring(0, 5) : ''),
                isActive: true
              })).filter(s => s.startTime && s.endTime);
            }
          });
        }
        setFormData({
          name: prefill.name || '',
          country: prefill.country || '',
          timezone: prefill.timezone
            ? normalizeTimezoneForFormSelect(prefill.timezone)
            : (prefill.country ? getTimezone(prefill.country) : ''),
          batch_type: prefill.batch_type || 'group',
          age_group: prefill.age_group || '',
          max_capacity: prefill.max_capacity ?? 10,
          start_date: prefill.start_date || new Date().toISOString().split('T')[0],
          end_date: prefill.end_date || '',
          teacher_id: prefill.teacher_id ? String(prefill.teacher_id) : '',
          secondary_teacher_id: '',
          is_inclusive: prefill.is_inclusive ?? false,
          status: 'active'
        });
        setSchedules(convertedSchedules);
        if (prefill.teacher_timezone) setSelectedTeacherTimezone(prefill.teacher_timezone);
      } else {
        setFormData({
          name: '',
          country: '',
          timezone: '',
          batch_type: 'group',
          age_group: '',
          max_capacity: 10,
          start_date: new Date().toISOString().split('T')[0],
          end_date: '',
          teacher_id: '',
          secondary_teacher_id: '',
          is_inclusive: false,
          status: 'active'
        });
        setSchedules({ 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] });
        setSelectedTeacherTimezone('');
      }
    }
  }, [mode, initialData, open, prefill]);

  // Fetch teacher suggestions only when the user opens Change / Find
  useEffect(() => {
    if (!open || !teacherPicker) return undefined;

    const scheduleData = buildSchedulePayload(schedules);
    if (scheduleData.length === 0) {
      setAvailableTeachers([]);
      setPartialTeachers([]);
      setUnavailableTeachers([]);
      return undefined;
    }

    let cancelled = false;
    setLoadingAvailableTeachers(true);
    batchesAPI
      .getAvailableTeachersForSchedules(scheduleData, {
        excludeBatchId: mode === 'edit' ? batchId : null,
      })
      .then((response) => {
        if (cancelled) return;
        const parsed = parseSubstituteTeachersResponse(response);
        setAvailableTeachers(parsed.available || []);
        setPartialTeachers(parsed.partial || []);
        setUnavailableTeachers(parsed.unavailable || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching available teachers:', err);
        setAvailableTeachers([]);
        setPartialTeachers([]);
        setUnavailableTeachers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAvailableTeachers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [teacherPicker, schedules, mode, open, batchId]);

  useEffect(() => {
    if (!open || mode !== 'create') {
      setNamePreview('');
      return;
    }
    if (!formData.country) {
      setNamePreview('');
      return;
    }
    let cancelled = false;
    batchesAPI
      .getNextName(formData.country)
      .then((res) => {
        if (cancelled) return;
        setNamePreview(res?.data?.data?.name || '');
      })
      .catch(() => {
        if (!cancelled) setNamePreview('');
      });
    return () => {
      cancelled = true;
    };
  }, [formData.country, mode, open]);

  // Update teacher timezone when teacher is selected
  useEffect(() => {
    if (formData.teacher_id) {
      const selectedTeacher = teachersById[String(formData.teacher_id)];
      if (selectedTeacher?.timezone) {
        setSelectedTeacherTimezone(selectedTeacher.timezone);
      } else if (mode === 'edit' && String(initialData?.teacher_id) === String(formData.teacher_id) && initialData?.teacher_timezone) {
        setSelectedTeacherTimezone(initialData.teacher_timezone);
      } else if (mode === 'create' && prefill?.teacher_timezone && String(prefill.teacher_id) === String(formData.teacher_id)) {
        setSelectedTeacherTimezone(prefill.teacher_timezone);
      }
    } else {
      setSelectedTeacherTimezone('');
    }
  }, [formData.teacher_id, teachersById, mode, initialData?.teacher_id, initialData?.teacher_timezone, prefill]);

  const ageGroupOptions = getAgeGroupSelectOptions(formData.age_group);
  const batchTypes = [
    { value: 'group', label: 'Group' },
    { value: 'one_on_one', label: 'One-on-One' }
  ];

  const handleSubmit = async () => {
    // Validation
    if (mode === 'create' && !formData.country) {
      setError('Country is required');
      return;
    }
    if (mode === 'edit' && !formData.name.trim()) {
      setError('Batch name is required');
      return;
    }
    if (!formData.age_group) {
      setError('Age group is required');
      return;
    }
    const hasSchedules = Object.values(schedules).some(slots => slots.length > 0);
    if (!hasSchedules) {
      setError('At least one time slot must be configured');
      return;
    }
    // Teacher is now optional - can be assigned later
    if (
      formData.teacher_id &&
      formData.secondary_teacher_id &&
      formData.teacher_id === formData.secondary_teacher_id
    ) {
      setError('Primary and secondary teacher must be different');
      return;
    }

    // In edit mode, validate capacity
    if (mode === 'edit') {
      const newCapacity = parseInt(formData.max_capacity);
      if (newCapacity < currentStudentCount) {
        setError(`Cannot reduce capacity below current student count (${currentStudentCount} students currently assigned)`);
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Convert schedules to backend format
      const scheduleData = [];
      Object.entries(schedules).forEach(([day, slots]) => {
        slots.forEach(slot => {
          if (slot.startTime && slot.endTime) {
            const startMinutes = toMinutes(slot.startTime);
            const endMinutes = toMinutes(slot.endTime);
            const durationMinutes = endMinutes - startMinutes;
            if (durationMinutes <= 0) {
              throw new Error(`Invalid time range for day ${day}: start must be before end`);
            }
            scheduleData.push({
              day_of_week: parseInt(day),
              start_time: slot.startTime + ':00',
              end_time: slot.endTime + ':00',
              duration_minutes: durationMinutes
            });
          }
        });
      });

      const storedTimezone = formData.timezone
        ? normalizeTimezoneForStorage(formData.timezone)
        : '';

      if (mode === 'edit' && batchId) {
        // Update batch basic info. A blank timezone keeps the value already saved.
        const updateData = {
          name: formData.name,
          country: formData.country || undefined,
          ...(storedTimezone ? { timezone: storedTimezone } : {}),
          batch_type: formData.batch_type,
          age_group: formData.age_group,
          max_capacity: parseInt(formData.max_capacity),
          start_date: formData.start_date,
          teacher_id: formData.teacher_id || null,
          secondary_teacher_id: formData.secondary_teacher_id || null,
          is_inclusive: formData.is_inclusive,
          status: formData.status,
          // Always send end_date so clearing the field persists as NULL
          end_date: formData.end_date || null
        };

        await batchesAPI.update(batchId, updateData);
        
        // Update schedules
        await batchesAPI.updateSchedules(batchId, scheduleData);
        
        onSuccess();
        handleClose();
      } else {
        // Create batch - don't send status, it defaults to 'active' on backend.
        // A blank timezone is filled on the server from the country's usual zone.
        const { status, name, timezone: _timezone, ...createData } = formData;
        const response = await batchesAPI.create({
          ...createData,
          country: formData.country,
          ...(storedTimezone ? { timezone: storedTimezone } : {}),
          schedules: scheduleData
        });

        const createdBatch = response?.data?.data || response?.data;
        onSuccess(createdBatch);
        handleClose();
      }
    } catch (error) {
      const apiMessage = error.response?.data?.message;
      setError(apiMessage || error.message || 'Failed to create batch');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (mode === 'create') {
      setFormData({
        name: '',
        country: '',
        timezone: '',
        batch_type: 'group',
        age_group: '',
        max_capacity: 10,
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        teacher_id: '',
        secondary_teacher_id: '',
        is_inclusive: false,
        status: 'active'
      });
      setSchedules({
        0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: []
      });
      setSelectedTeacherTimezone('');
      setAvailableTeachers([]);
      setPartialTeachers([]);
      setUnavailableTeachers([]);
    }
    setTeacherPicker(null);
    setError('');
    onClose();
  };

  const addTimeSlot = (day, startTime = '', endTime = '') => {
    setSchedules((prev) => ({
      ...prev,
      [day]: [...prev[day], { startTime, endTime, isActive: true }],
    }));
  };

  const commitAddSlot = () => {
    if (!addSlotStart || !addSlotEnd) {
      setError('Start and end time are required');
      return;
    }
    if (toMinutes(addSlotEnd) <= toMinutes(addSlotStart)) {
      setError('Start must be before end');
      return;
    }
    setError('');
    addTimeSlot(addSlotDay, addSlotStart, addSlotEnd);
  };

  const updateTimeSlot = (day, index, field, value) => {
    setSchedules(prev => ({
      ...prev,
      [day]: prev[day].map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const removeTimeSlot = (day, index) => {
    setSchedules(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index)
    }));
  };

  const handleUnassignStudent = async (assignmentId, payload) => {
    if (!assignmentId || !batchId) return;

    setUnassigningAssignmentId(assignmentId);
    try {
      const response = await allocationsAPI.unassignOnLeave(assignmentId, payload || {});
      toast.success(response?.data?.message || 'Student unassigned');
      queryClient.invalidateQueries(['batch', batchId]);
      queryClient.invalidateQueries(['batches']);
    } catch (err) {
      console.error('Unassign error:', err);
      toast.error(err.response?.data?.message || 'Failed to unassign student');
      throw err;
    } finally {
      setUnassigningAssignmentId(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Group />
          {mode === 'edit' ? 'Edit Batch' : 'Create New Batch'}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required={mode === 'create'}>
              <InputLabel>Country</InputLabel>
              <Select
                value={formData.country || ''}
                onChange={(e) => {
                  const country = e.target.value;
                  setFormData((prev) => {
                    const previousDefault = prev.country ? getTimezone(prev.country) : '';
                    const nextDefault = country ? getTimezone(country) : '';
                    const keepTimezone = prev.timezone && prev.timezone !== previousDefault;
                    return {
                      ...prev,
                      country,
                      timezone: keepTimezone ? prev.timezone : nextDefault,
                    };
                  });
                }}
                label="Country"
              >
                {getCountries().map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Timezone (optional)</InputLabel>
              <Select
                value={formData.timezone ? normalizeTimezoneForFormSelect(formData.timezone) : ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, timezone: e.target.value }))}
                label="Timezone (optional)"
              >
                {getAvailableTimezones().map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choosing a country fills this with that country’s usual zone. On edit, leave it blank to keep the saved timezone.
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            {mode === 'create' ? (
              <TextField
                fullWidth
                label="Batch name"
                value={namePreview || (formData.country ? 'Assigning next number…' : '')}
                placeholder="Select a country to generate the name"
                InputProps={{ readOnly: true }}
                helperText="Each country has its own series (USA318, UK713). The number is the highest existing name for that country, plus 1."
              />
            ) : (
              <TextField
                fullWidth
                label="Batch name"
                value={formData.name}
                InputProps={{ readOnly: true }}
                helperText="Name stays as created. Country can still be updated."
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Batch Type</InputLabel>
              <Select
                value={formData.batch_type}
                onChange={(e) => setFormData(prev => ({ ...prev, batch_type: e.target.value }))}
                label="Batch Type"
              >
                {batchTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Age Group</InputLabel>
              <Select
                value={formData.age_group}
                onChange={(e) => setFormData(prev => ({ ...prev, age_group: e.target.value }))}
                label="Age Group"
              >
                {ageGroupOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Maximum Capacity"
              type="number"
              value={formData.max_capacity}
              onChange={(e) => setFormData(prev => ({ ...prev, max_capacity: e.target.value }))}
              inputProps={{ 
                min: mode === 'edit' ? Math.max(currentStudentCount, 1) : 1, 
                max: 50 
              }}
              required
              helperText={mode === 'edit' ? `Current: ${currentStudentCount} students assigned` : ''}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date (Optional)"
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: formData.start_date }}
            />
          </Grid>

          {mode === 'edit' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="to_start">To Start</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
                <FormHelperText>
                  Inactive, completed, or cancelled is blocked until every student is unassigned.
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <input
                type="checkbox"
                id="is_inclusive"
                checked={formData.is_inclusive}
                onChange={(e) => setFormData(prev => ({ ...prev, is_inclusive: e.target.checked }))}
              />
              <label htmlFor="is_inclusive">
                <Typography variant="body2">
                  Inclusive batch (for special needs students)
                </Typography>
              </label>
            </Box>
          </Grid>

          {mode === 'edit' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <BatchAssignedStudentsPanel
                batch={liveBatch || initialData}
                students={assignedStudents}
                loading={loadingAssignedStudents}
                variant="compact"
                title={`Assigned Students (${currentStudentCount})`}
                canManageAssignments={canManageAssignments}
                onUnassign={handleUnassignStudent}
                unassigningAssignmentId={unassigningAssignmentId}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
              Schedule
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Times are in IST (UTC+05:30). Only days with a slot are listed.
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {DAY_SHORT.map((label, dayIndex) => {
                const filled = (schedules[dayIndex] || []).length > 0;
                return (
                  <Chip
                    key={label}
                    label={label}
                    color={filled ? 'primary' : 'default'}
                    variant={filled ? 'filled' : 'outlined'}
                    onClick={() => setAddSlotDay(dayIndex)}
                    sx={{ fontWeight: addSlotDay === dayIndex ? 700 : 400 }}
                  />
                );
              })}
            </Stack>
            {configuredSlots.length === 0 ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>No time slots yet. Add one below.</Alert>
            ) : (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {configuredSlots.map((slot) => (
                  <Box
                    key={`${slot.day}-${slot.index}`}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Chip size="small" label={DAY_SHORT[slot.day]} />
                    <TimeSelector
                      label="Start"
                      size="small"
                      fullWidth={false}
                      value={slot.startTime}
                      onChange={(value) => updateTimeSlot(slot.day, slot.index, 'startTime', value)}
                    />
                    <Typography variant="body2">to</Typography>
                    <TimeSelector
                      label="End"
                      size="small"
                      fullWidth={false}
                      value={slot.endTime}
                      onChange={(value) => updateTimeSlot(slot.day, slot.index, 'endTime', value)}
                    />
                    <Button size="small" color="error" onClick={() => removeTimeSlot(slot.day, slot.index)}>
                      Remove
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Day</InputLabel>
                <Select
                  value={addSlotDay}
                  label="Day"
                  onChange={(e) => setAddSlotDay(Number(e.target.value))}
                >
                  {DAY_NAMES.map((name, idx) => (
                    <MenuItem key={name} value={idx}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TimeSelector
                label="Start"
                size="small"
                fullWidth={false}
                value={addSlotStart}
                onChange={setAddSlotStart}
              />
              <TimeSelector
                label="End"
                size="small"
                fullWidth={false}
                value={addSlotEnd}
                onChange={setAddSlotEnd}
              />
              <Button variant="outlined" onClick={commitAddSlot}>Add slot</Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              <LabelWithInfo
                label="Primary teacher"
                title="Main teacher"
                body="Teaches this batch by default. Suggestions load only when you click Change or Find."
              />
            </Typography>
            {(() => {
              const primary = assignedTeacher('primary');
              return primary ? (
                <Alert severity={primary.isActive ? 'success' : 'warning'} sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">{primary.name}</Typography>
                    <Chip size="small" label={primary.isActive ? 'Active' : 'Inactive'} color={primary.isActive ? 'success' : 'error'} />
                    {!primary.isActive ? (
                      <Typography variant="caption">Time can still be saved. Clear or replace before assigning a new teacher.</Typography>
                    ) : null}
                  </Stack>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>No primary teacher assigned.</Alert>
              );
            })()}
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                size="small"
                variant={teacherPicker === 'primary' ? 'contained' : 'outlined'}
                disabled={!hasConfiguredSchedules}
                onClick={() => setTeacherPicker((prev) => (prev === 'primary' ? null : 'primary'))}
              >
                {formData.teacher_id ? 'Change primary' : 'Find primary'}
              </Button>
              {formData.teacher_id ? (
                <Button size="small" onClick={() => setFormData((prev) => ({ ...prev, teacher_id: '' }))}>
                  Clear
                </Button>
              ) : null}
            </Stack>
            {!hasConfiguredSchedules ? (
              <Typography variant="body2" color="text.secondary">Add a time slot first if you want to search who is free.</Typography>
            ) : null}
            {teacherPicker === 'primary' ? (
              <BatchScheduleTeacherResults
                exact={pickerLists.available}
                partial={pickerLists.partial}
                unavailable={pickerLists.unavailable}
                loading={loadingAvailableTeachers}
                value={formData.teacher_id || ''}
                onChange={(teacherId) => {
                  setFormData((prev) => ({
                    ...prev,
                    teacher_id: teacherId || '',
                    secondary_teacher_id:
                      teacherId && String(prev.secondary_teacher_id) === String(teacherId)
                        ? ''
                        : prev.secondary_teacher_id,
                  }));
                  if (teacherId) setTeacherPicker(null);
                }}
                allowEmpty
                emptyLabel={mode === 'create' ? 'No teacher yet — assign later' : 'None'}
                excludeIds={[formData.secondary_teacher_id].filter(Boolean)}
                emptyMessage="No teachers found for the configured time slots."
              />
            ) : null}
            {selectedTeacherTimezone && formData.teacher_id && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Timezone:</strong> All times will be configured in {selectedTeacherTimezone} timezone
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Location:</strong> {getTimezoneLocation(selectedTeacherTimezone)}
                </Typography>
              </Alert>
            )}
          </Grid>

          {mode === 'edit' && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                <LabelWithInfo
                  label="Secondary teacher (optional)"
                  title="Backup teacher"
                  body="Optional backup. Suggestions load only when you click Change."
                />
              </Typography>
              {(() => {
                const secondary = assignedTeacher('secondary');
                return secondary ? (
                  <Alert severity={secondary.isActive ? 'info' : 'warning'} sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">{secondary.name}</Typography>
                      <Chip size="small" label={secondary.isActive ? 'Active' : 'Inactive'} color={secondary.isActive ? 'success' : 'error'} />
                    </Stack>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 1 }}>No secondary teacher.</Alert>
                );
              })()}
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  variant={teacherPicker === 'secondary' ? 'contained' : 'outlined'}
                  disabled={!hasConfiguredSchedules}
                  onClick={() => setTeacherPicker((prev) => (prev === 'secondary' ? null : 'secondary'))}
                >
                  {formData.secondary_teacher_id ? 'Change secondary' : 'Find secondary'}
                </Button>
                {formData.secondary_teacher_id ? (
                  <Button size="small" onClick={() => setFormData((prev) => ({ ...prev, secondary_teacher_id: '' }))}>
                    Clear
                  </Button>
                ) : null}
              </Stack>
              {teacherPicker === 'secondary' ? (
                <BatchScheduleTeacherResults
                  exact={pickerLists.available}
                  partial={pickerLists.partial}
                  unavailable={pickerLists.unavailable}
                  loading={loadingAvailableTeachers}
                  value={formData.secondary_teacher_id || ''}
                  onChange={(teacherId) => {
                    setFormData((prev) => ({
                      ...prev,
                      secondary_teacher_id: teacherId || '',
                    }));
                    if (teacherId) setTeacherPicker(null);
                  }}
                  allowEmpty
                  emptyLabel="None"
                  excludeIds={[formData.teacher_id].filter(Boolean)}
                  emptyMessage="No teachers found for the configured time slots."
                />
              ) : null}
            </Grid>
          )}
        </Grid>
      </DialogContent>      {error && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update Batch' : 'Create Batch')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchCreationForm;
